import { useState, useMemo } from 'react';
import ToolCard from '../../../components/feature/ToolCard';
import { allTools, featuredTools } from '../../../mocks/tools';
import { TOOL_PRICING } from '../../../mocks/tool-pricing';

const CATEGORY_OPTIONS = [
  'All Categories',
  'AI Writing Tools',
  'AI Image Generators',
  'AI Video Tools',
  'AI Website Builders',
  'AI Marketing Tools',
  'AI Productivity Tools',
  'AI Coding Tools',
  'AI Design Tools',
  'AI Voice Tools',
  'AI Automation Tools',
];

const PRICING_OPTIONS = ['All Pricing', 'Free', 'Freemium', 'Paid'];

const SORT_OPTIONS = [
  { label: 'Newest First', value: 'newest' },
  { label: 'Most Popular', value: 'popular' },
  { label: 'A–Z', value: 'az' },
  { label: 'Z–A', value: 'za' },
];

const PAGE_SIZE = 12;

// Simple "popularity" weight — tools with badges rank higher
const popularityScore = (badge?: string) => {
  if (badge === 'featured') return 3;
  if (badge === 'trending') return 2;
  if (badge === 'new') return 1;
  return 0;
};

export default function ToolsDirectory() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All Categories');
  const [activePricing, setActivePricing] = useState('All Pricing');
  const [sortBy, setSortBy] = useState('newest');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const [catOpen, setCatOpen] = useState(false);
  const [priceOpen, setPriceOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  // Build set of slugs already displayed in other dedicated homepage sections
  // so the Directory never shows a tool that already appears above on the page.
  const shownElsewhereSlugs = useMemo(() => {
    const set = new Set<string>();
    // Featured Tools section
    featuredTools.forEach((t) => set.add(t.slug));
    // New Tools This Week section (badge === 'new')
    allTools.forEach((t) => {
      if (t.badge === 'new' || t.badge === 'trending') set.add(t.slug);
    });
    return set;
  }, []);

  // Only tools not already highlighted in another section
  const directoryPool = useMemo(
    () => allTools.filter((t) => !shownElsewhereSlugs.has(t.slug)),
    [shownElsewhereSlugs]
  );

  const filteredTools = useMemo(() => {
    let tools = directoryPool.filter((tool) => {
      const matchesSearch =
        tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tool.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tool.category.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory =
        activeCategory === 'All Categories' || tool.category === activeCategory;

      const pricingType = TOOL_PRICING[tool.slug];
      const matchesPricing =
        activePricing === 'All Pricing' || pricingType === activePricing;

      return matchesSearch && matchesCategory && matchesPricing;
    });

    switch (sortBy) {
      case 'az':
        tools = [...tools].sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'za':
        tools = [...tools].sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'popular':
        tools = [...tools].sort(
          (a, b) => popularityScore(b.badge) - popularityScore(a.badge)
        );
        break;
      case 'newest':
      default:
        tools = [...tools].sort(
          (a, b) => popularityScore(b.badge) - popularityScore(a.badge)
        );
        break;
    }

    return tools;
  }, [searchQuery, activeCategory, activePricing, sortBy, directoryPool]);

  const visibleTools = filteredTools.slice(0, visibleCount);
  const hasMore = visibleCount < filteredTools.length;

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + PAGE_SIZE);
  };

  const handleReset = () => {
    setSearchQuery('');
    setActiveCategory('All Categories');
    setActivePricing('All Pricing');
    setSortBy('newest');
    setVisibleCount(PAGE_SIZE);
  };

  const handleCategoriesClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const element = document.getElementById('categories');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const closeAllDropdowns = () => {
    setCatOpen(false);
    setPriceOpen(false);
    setSortOpen(false);
  };

  const currentSortLabel =
    SORT_OPTIONS.find((o) => o.value === sortBy)?.label ?? 'Sort By';

  const hasActiveFilters =
    activeCategory !== 'All Categories' ||
    activePricing !== 'All Pricing' ||
    searchQuery.trim() !== '';

  return (
    <section
      id="tools-directory"
      className="py-32 px-4 sm:px-6 lg:px-8 bg-[#0a0a0a] relative"
      onClick={closeAllDropdowns}
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gray-700/60 to-transparent" />

      <div className="max-w-7xl mx-auto">
        {/* ── Section Header ── */}
        <div className="text-center mb-16">
          <span className="inline-block px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-teal-400 text-xs font-semibold tracking-widest uppercase mb-5">
            Full Directory
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-5 tracking-tight leading-tight">
            AI Tools Directory
          </h2>
          <p className="text-base text-gray-500 max-w-2xl mx-auto leading-relaxed">
            Browse our complete collection of AI tools to boost your productivity and creativity.
          </p>
        </div>

        {/* ── Search Bar ── */}
        <div className="mb-6 max-w-2xl mx-auto">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg" />
            <input
              type="text"
              placeholder="Search AI tools by name, use case, or category…"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setVisibleCount(PAGE_SIZE); }}
              className="w-full pl-12 pr-12 py-3.5 bg-gray-900/60 border border-gray-800/80 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all duration-300"
            />
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(''); setVisibleCount(PAGE_SIZE); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            )}
          </div>
        </div>

        {/* ── Filter + Sort Bar ── */}
        <div className="mb-8 flex flex-wrap items-center gap-3 justify-between">
          {/* Filters left side */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Category Dropdown */}
            <div
              className="relative"
              onClick={(e) => { e.stopPropagation(); setCatOpen((p) => !p); setPriceOpen(false); setSortOpen(false); }}
            >
              <button className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${activeCategory !== 'All Categories' ? 'bg-teal-500/20 border-teal-500/50 text-teal-300' : 'bg-gray-900/60 border-gray-800/80 text-gray-300 hover:border-teal-500/40 hover:text-white'}`}>
                <i className="ri-grid-line text-base" />
                {activeCategory === 'All Categories' ? 'Category' : activeCategory}
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${catOpen ? 'rotate-180' : ''}`} />
              </button>
              {catOpen && (
                <div className="absolute left-0 top-full mt-2 w-56 bg-[#111827] border border-gray-800/80 rounded-xl shadow-xl z-50 py-1 overflow-hidden">
                  {CATEGORY_OPTIONS.map((cat) => (
                    <button
                      key={cat}
                      onClick={(e) => { e.stopPropagation(); setActiveCategory(cat); setVisibleCount(PAGE_SIZE); setCatOpen(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors duration-150 cursor-pointer ${activeCategory === cat ? 'text-teal-400 bg-teal-500/10' : 'text-gray-300 hover:bg-gray-800/60 hover:text-white'}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Pricing Dropdown */}
            <div
              className="relative"
              onClick={(e) => { e.stopPropagation(); setPriceOpen((p) => !p); setCatOpen(false); setSortOpen(false); }}
            >
              <button className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${activePricing !== 'All Pricing' ? 'bg-teal-500/20 border-teal-500/50 text-teal-300' : 'bg-gray-900/60 border-gray-800/80 text-gray-300 hover:border-teal-500/40 hover:text-white'}`}>
                <i className="ri-price-tag-3-line text-base" />
                {activePricing === 'All Pricing' ? 'Pricing' : activePricing}
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${priceOpen ? 'rotate-180' : ''}`} />
              </button>
              {priceOpen && (
                <div className="absolute left-0 top-full mt-2 w-44 bg-[#111827] border border-gray-800/80 rounded-xl shadow-xl z-50 py-1 overflow-hidden">
                  {PRICING_OPTIONS.map((opt) => (
                    <button
                      key={opt}
                      onClick={(e) => { e.stopPropagation(); setActivePricing(opt); setVisibleCount(PAGE_SIZE); setPriceOpen(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors duration-150 cursor-pointer ${activePricing === opt ? 'text-teal-400 bg-teal-500/10' : 'text-gray-300 hover:bg-gray-800/60 hover:text-white'}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Clear filters */}
            {hasActiveFilters && (
              <button
                onClick={(e) => { e.stopPropagation(); handleReset(); }}
                className="flex items-center gap-1.5 px-3 py-2.5 text-sm text-gray-500 hover:text-white transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-circle-line text-base" />
                Clear filters
              </button>
            )}
          </div>

          {/* Sort right side */}
          <div
            className="relative"
            onClick={(e) => { e.stopPropagation(); setSortOpen((p) => !p); setCatOpen(false); setPriceOpen(false); }}
          >
            <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-gray-800/80 bg-gray-900/60 text-gray-300 hover:border-teal-500/40 hover:text-white transition-all duration-200 cursor-pointer whitespace-nowrap">
              <i className="ri-sort-desc text-base" />
              {currentSortLabel}
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${sortOpen ? 'rotate-180' : ''}`} />
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-[#111827] border border-gray-800/80 rounded-xl shadow-xl z-50 py-1 overflow-hidden">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={(e) => { e.stopPropagation(); setSortBy(opt.value); setVisibleCount(PAGE_SIZE); setSortOpen(false); }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors duration-150 cursor-pointer ${sortBy === opt.value ? 'text-teal-400 bg-teal-500/10' : 'text-gray-300 hover:bg-gray-800/60 hover:text-white'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Tool Count Bar ── */}
        <div className="mb-8 flex items-center gap-3">
          <div className="flex items-center gap-2.5 px-4 py-2 bg-gray-900/40 border border-gray-800/60 rounded-xl">
            <i className="ri-apps-line text-teal-400 text-base" />
            <span className="text-sm font-semibold text-white">
              Showing <span className="text-teal-400">{visibleTools.length}</span>
              {filteredTools.length !== directoryPool.length ? (
                <> of <span className="text-teal-400">{filteredTools.length}</span> filtered</>
              ) : null}
              {' '}of <span className="text-teal-400">{directoryPool.length}</span> AI Tools
            </span>
          </div>

          {/* Active filter pills */}
          <div className="flex flex-wrap gap-2">
            {activeCategory !== 'All Categories' && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-teal-500/10 border border-teal-500/30 text-teal-300 text-xs font-semibold rounded-full">
                {activeCategory}
                <button onClick={() => { setActiveCategory('All Categories'); setVisibleCount(PAGE_SIZE); }} className="hover:text-white transition-colors cursor-pointer">
                  <i className="ri-close-line" />
                </button>
              </span>
            )}
            {activePricing !== 'All Pricing' && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-teal-500/10 border border-teal-500/30 text-teal-300 text-xs font-semibold rounded-full">
                {activePricing}
                <button onClick={() => { setActivePricing('All Pricing'); setVisibleCount(PAGE_SIZE); }} className="hover:text-white transition-colors cursor-pointer">
                  <i className="ri-close-line" />
                </button>
              </span>
            )}
          </div>
        </div>

        {/* ── Tools Grid ── */}
        {visibleTools.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visibleTools.map((tool) => (
                <ToolCard key={tool.id} tool={tool} />
              ))}
            </div>

            {/* ── Pagination ── */}
            <div className="mt-12 flex flex-col items-center gap-4">
              {/* Progress bar */}
              <div className="w-full max-w-md">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                  <span>Showing {visibleTools.length} of {filteredTools.length} tools</span>
                  <span>{Math.round((visibleTools.length / filteredTools.length) * 100)}% loaded</span>
                </div>
                <div className="h-1.5 w-full bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-teal-500 to-teal-400 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((visibleTools.length / filteredTools.length) * 100, 100)}%` }}
                  />
                </div>
              </div>

              {hasMore ? (
                <button
                  onClick={handleLoadMore}
                  className="inline-flex items-center gap-2.5 px-10 py-3.5 bg-teal-500/20 hover:bg-teal-500 text-teal-300 hover:text-white text-sm font-semibold rounded-xl transition-all duration-300 cursor-pointer border border-teal-500/40 hover:border-teal-500 hover:shadow-lg hover:shadow-teal-500/30 hover:scale-105 active:scale-95 whitespace-nowrap"
                >
                  <i className="ri-add-line text-base" />
                  Load More Tools
                  <span className="text-xs opacity-70">({filteredTools.length - visibleCount} remaining)</span>
                </button>
              ) : (
                <p className="text-sm text-gray-500 flex items-center gap-2">
                  <i className="ri-check-double-line text-teal-400" />
                  You&apos;ve seen all {filteredTools.length} tools
                </p>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-gray-900/60 rounded-full border border-gray-800/80">
              <i className="ri-search-line text-3xl text-gray-600" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">No tools found</h3>
            <p className="text-gray-500 text-sm max-w-md mx-auto mb-6">
              We couldn&apos;t find any tools matching your filters. Try adjusting your search or filters.
            </p>
            <button
              onClick={handleReset}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-teal-500/20 hover:bg-teal-500 text-teal-300 hover:text-white text-sm font-semibold rounded-lg transition-all duration-300 cursor-pointer border border-teal-500/40 hover:border-teal-500"
            >
              <i className="ri-refresh-line text-base" />
              Clear All Filters
            </button>
          </div>
        )}

        {/* ── CTA ── */}
        <div className="mt-20 relative overflow-hidden rounded-2xl border border-gray-800/80 bg-gray-900/60 backdrop-blur-sm p-14 text-center">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_70%_at_50%_50%,rgba(20,184,166,0.12),transparent)]" />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-teal-500/50 to-transparent" />
          <div className="relative z-10">
            <h3 className="text-3xl md:text-4xl font-extrabold text-white mb-4 tracking-tight">
              Discover More AI Tools
            </h3>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto leading-relaxed text-base">
              Explore our complete directory and find the perfect AI tools to transform your workflow.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href="#categories"
                onClick={handleCategoriesClick}
                className="inline-flex items-center gap-2.5 px-8 py-3.5 bg-teal-500 hover:bg-teal-400 text-white text-sm font-semibold rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-teal-500/30 hover:shadow-teal-400/40 hover:scale-105 active:scale-95"
              >
                Browse All Categories
                <i className="ri-arrow-right-line text-base" />
              </a>
              <a
                href="#categories"
                onClick={handleCategoriesClick}
                className="inline-flex items-center gap-2.5 px-8 py-3.5 bg-transparent hover:bg-teal-500/10 text-teal-400 hover:text-teal-300 text-sm font-semibold rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer border border-teal-500/40 hover:border-teal-500/60"
              >
                View Categories
                <i className="ri-grid-line text-base" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gray-700/60 to-transparent" />
    </section>
  );
}
